import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, AlertCircle, Stethoscope } from 'lucide-react'
import { API_SERVER_URL } from '@/lib/apiConfig';

const API_BASE_URL = API_SERVER_URL;

interface PortalData {
  auth_link: string
  status: string
  program: string
  clinic_name: string
}

/**
 * Public page that embeds the MDI patient portal in an iframe.
 * Accessed via email link sent to patient after case creation.
 * No authentication required - patient ID is the access token.
 */
export default function MdiPortal() {
  const { patientId } = useParams<{ patientId: string }>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [portalData, setPortalData] = useState<PortalData | null>(null)

  useEffect(() => {
    const fetchPortalData = async () => {
      if (!patientId) {
        setError('Invalid portal link')
        setLoading(false)
        return
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/mdi/public/portal/${patientId}`)
        const data = await response.json()

        if (!response.ok || !data.success) {
          setError(data.error?.message || 'Failed to load portal')
          setLoading(false)
          return
        }

        setPortalData(data.data)
        setLoading(false)
      } catch (err) {
        console.error('Failed to fetch portal data:', err)
        setError('Unable to connect to the portal. Please try again later.')
        setLoading(false)
      }
    }

    fetchPortalData()
  }, [patientId])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600 text-lg">Loading your patient portal...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Unable to Load Portal</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <p className="text-sm text-slate-500">
            If you continue to experience issues, please contact your healthcare provider.
          </p>
        </div>
      </div>
    )
  }

  if (!portalData?.auth_link) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Stethoscope className="h-8 w-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Portal Not Available</h1>
          <p className="text-slate-600 mb-6">
            The patient portal is not yet available for this consultation. 
            Please check back later or contact your healthcare provider.
          </p>
          <div className="bg-amber-50 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Case Status:</strong> {portalData?.status || 'Unknown'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const clinicName = portalData.clinic_name || 'Healthcare Provider'

  // Format program name for display
  const formatProgram = (program: string): string => {
    const programLabels: Record<string, string> = {
      'glp1': 'Weight Management (GLP-1)',
      'weightwise': 'Weight Management (GLP-1)',
      'trt': 'Testosterone Therapy',
      'hrt': 'Hormone Therapy',
      'peptide': 'Peptide Therapy',
      'nad': 'NAD+ Therapy',
      'fertility': 'Fertility',
      'general': 'General Consultation',
      'telehealth': 'Telehealth Consultation',
    }
    return programLabels[program?.toLowerCase()] || program || 'Consultation'
  }

  // Format status for display
  const formatStatus = (status: string): string => {
    const statusLabels: Record<string, string> = {
      'draft': 'Draft',
      'submitted': 'Submitted',
      'pending': 'Pending Review',
      'in_review': 'In Review',
      'completed': 'Completed',
      'cancelled': 'Cancelled',
    }
    return statusLabels[status?.toLowerCase()] || status?.replace(/_/g, ' ') || 'Pending'
  }

  // Get status badge color
  const getStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-700'
      case 'in_review':
        return 'bg-blue-100 text-blue-700'
      case 'submitted':
      case 'pending':
        return 'bg-amber-100 text-amber-700'
      case 'cancelled':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-slate-100 text-slate-700'
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header with Nimbus Providers Branding + Clinic Name */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Stethoscope className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Nimbus Providers Patient Portal</h1>
            <p className="text-xs text-slate-500">{clinicName} • Secure Telehealth Consultation</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full">
            {formatProgram(portalData.program)}
          </span>
          <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(portalData.status)}`}>
            {formatStatus(portalData.status)}
          </span>
        </div>
      </header>

      {/* Iframe Container - crops MDI header and footer */}
      <div className="flex-1 relative overflow-hidden">
        <iframe
          src={portalData.auth_link}
          title="MDI Patient Portal"
          className="absolute w-full border-0"
          style={{
            height: 'calc(100% + 200px)', // Extra height to account for cropped areas
            top: '-100px', // Shift up to hide NIMBUS header
            left: 0,
          }}
          allow="camera; microphone; fullscreen"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
        />
      </div>
    </div>
  )
}
